/**
 * `MessageInput` — the chat composer for the Secret Chat conversation view.
 *
 * Responsibilities (requirements coverage R4.1, R4.4):
 *   - Render a multi-line textarea + send button. Enter alone submits; Shift+
 *     Enter inserts a newline; Enter while an IME composition is active does
 *     not submit (Japanese / CJK users routinely confirm candidates with
 *     Enter).
 *   - Client-side pre-validation: trim length must be 1..maxLength. Zero
 *     (or whitespace-only) keeps the submit button disabled AND makes Enter
 *     a no-op so an accidental keystroke can never burn an API round-trip.
 *     `maxLength` overflow disables submit and surfaces an inline error so
 *     the user can shorten the message before retrying.
 *   - Optimistic send: submitting hands the content to `onSend` and clears
 *     the textarea immediately so the user can keep typing without waiting
 *     for the server. The message itself appears in the transcript as a
 *     "送信中" bubble (owned by `useMessages` / `MessageList`), and send
 *     failures surface there with a retry affordance — so this component no
 *     longer blocks on the request or renders send errors inline. Clearing
 *     on submit also doubles as the double-submit guard: a second Enter sees
 *     an empty field and is a no-op.
 *
 * Dependency direction (`design.md` §Dependency Direction):
 *   This component depends only on the `onSend` callback injected by its
 *   parent (`ChatView`). It does NOT import `createChatApi`, `fetch`, or any
 *   hook — the parent wires `useMessages.sendMessage` in. That keeps the
 *   component trivially testable and reusable.
 *
 * Test strategy: see `MessageInput.test.tsx`. The component is exercised via
 * `@testing-library/react`'s `render` + `fireEvent` (the project does not
 * depend on `@testing-library/user-event`).
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ChangeEvent,
  FormEvent,
  JSX,
  KeyboardEvent,
} from 'react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface MessageInputProps {
  /**
   * Optimistic send handler. Called with the composed content when the user
   * submits. The parent (`ChatView`) wires this to `useMessages.sendMessage`,
   * which adds the optimistic bubble and reconciles success/failure. The
   * return value is ignored here — this component does not await it.
   */
  readonly onSend: (content: string) => void;
  /**
   * Server-aligned max length. Default mirrors `api/src/domain/validation.ts`
   * (`validateMessageContent`). Kept as a prop so tests can drive the
   * boundary without typing 2001 characters.
   */
  readonly maxLength?: number;
  /**
   * When non-null, the composer is rendered disabled and the string is
   * displayed as the reason (e.g. guest message limit reached). The send
   * button and textarea are both disabled regardless of `content`.
   */
  readonly blockedReason?: string | null;
  /**
   * Optional informational hint shown below the textarea — used to display
   * remaining quota for guests under a `guestMessageLimit`. Doesn't disable
   * anything; pure UI affordance.
   */
  readonly quotaHint?: string | null;
  /**
   * Room owner's nickname. Used to personalize the placeholder shown to
   * guests while blocked. When undefined we fall back to the literal
   * "オーナー".
   */
  readonly ownerNickname?: string;
  /**
   * 外部 (ハンバーガーメニューのテンプレート選択モーダル等) から
   * テキスト挿入を要求するためのフック。`nonce` の値が変化したときに
   * `text` を現在の入力末尾に追記する。
   *
   * - 入力欄が空 / 末尾が改行のときはそのまま追記
   * - それ以外は末尾に改行を 1 つ挟んで追記
   *
   * `nonce` ベースなのは、同一テンプレートを連続で選んだ場合でも値が
   * 同じでイベントが発火しない問題を避けるため (caller が必ず nonce を
   * 増やす運用)。
   */
  readonly pendingInsert?: { readonly text: string; readonly nonce: number } | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Mirror of the server's message body max length
 * (`api/src/domain/validation.ts`). Kept as a local default rather than
 * imported so the UI component stays decoupled from the domain module — if
 * the server bumps the cap, the worst case is a server-side 400 surfaced as a
 * failed bubble.
 */
const DEFAULT_MAX_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MessageInput(props: MessageInputProps): JSX.Element {
  const { onSend, ownerNickname } = props;
  const maxLength = props.maxLength ?? DEFAULT_MAX_LENGTH;
  const blockedReason = props.blockedReason ?? null;
  const quotaHint = props.quotaHint ?? null;
  const pendingInsert = props.pendingInsert ?? null;

  // Form state. With optimistic send there is no in-flight "submitting" state
  // to track here — the transcript owns the message's lifecycle.
  const [content, setContent] = useState('');

  // `textareaRef` lets us restore focus to the textarea after a send. The DOM
  // autofocus behavior is unreliable across browsers when the value changes
  // programmatically, so we drive it explicitly.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // テキストエリアのオートグロー:
  //   - 初期 / クリア時は 1 行ぶんの高さに収まる
  //   - 改行が入った瞬間に scrollHeight ぶんだけ縦に伸びる
  //   - CSS 側で max-height を持たせているので、無限には伸びない
  // useLayoutEffect を使うのは描画前に高さを確定させて、入力中のがた付き
  // を防ぐため。
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    // 一度 auto に戻してから scrollHeight を取ると、行数が減ったときに
    // 縮むようになる (放置すると最大行のままになる)。
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [content]);

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  /**
   * `trimmedLength` is the canonical "is there something to send" signal.
   * We trim because whitespace-only content is rejected by the server (R4.4)
   * and even an accidentally-pasted space shouldn't enable the submit
   * button. `content.length` (not trimmed) is what we compare against
   * `maxLength` because the server does the same — a long message with
   * trailing whitespace would still exceed the cap.
   */
  const trimmedLength = useMemo(() => content.trim().length, [content]);
  const tooLong = content.length > maxLength;
  const blocked = blockedReason !== null;
  const canSubmit = trimmedLength > 0 && !tooLong && !blocked;

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [],
  );

  // 外部からのテンプレート挿入要求を反映する。`pendingInsert.nonce` が
  // 変化したタイミングでだけ追記する。マウント時の最初の nonce は実際の
  // 挿入要求ではないため、初回マウント時は無視する (lastSeenNonceRef で
  // 監視)。
  const lastSeenNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (pendingInsert === null) return;
    if (lastSeenNonceRef.current === pendingInsert.nonce) return;
    lastSeenNonceRef.current = pendingInsert.nonce;

    const text = pendingInsert.text;
    if (text.length === 0) return;

    setContent((prev) => {
      if (prev.length === 0) return text;
      return prev.endsWith('\n') ? `${prev}${text}` : `${prev}\n${text}`;
    });
    // textarea にフォーカスを戻して、ユーザーが続けて追記しやすくする。
    queueMicrotask(() => {
      textareaRef.current?.focus();
    });
  }, [pendingInsert]);

  /**
   * Submit the current content optimistically. Centralized so both the
   * form's `onSubmit` (button click) and `handleKeyDown` (Enter) can call it.
   *
   * Clears the textarea immediately and hands the snapshot to `onSend`. The
   * immediate clear is also the double-submit guard: a duplicate Enter sees
   * an empty field and bails via `canSubmit`.
   */
  const performSubmit = useCallback(() => {
    if (!canSubmit) return;

    // Snapshot before clearing so a fast follow-up keystroke can't truncate
    // the message we hand to `onSend`.
    const payload = content;
    setContent('');
    // Restore focus so the user can keep typing without reaching for the
    // mouse. `null`-guard because tests don't always attach a ref.
    textareaRef.current?.focus();
    onSend(payload);
  }, [canSubmit, content, onSend]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      performSubmit();
    },
    [performSubmit],
  );

  /**
   * Enter submits, Shift+Enter inserts a newline, Enter during IME
   * composition is a no-op (the user is confirming a candidate, not
   * sending the message). The `isComposing` check is critical for
   * Japanese / Chinese / Korean users — without it, every kanji
   * confirmation would post the message-in-progress.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter') return;
      if (e.shiftKey) return;
      // React synthesizes `isComposing`; some test runners pass the
      // initializer value verbatim via `fireEvent.keyDown(..., { isComposing: true })`.
      // `nativeEvent.isComposing` is the spec-aligned source of truth, with
      // a fallback for synthetic test events that only set the React prop.
      const native = e.nativeEvent as KeyboardEvent['nativeEvent'] & {
        isComposing?: boolean;
      };
      if (native.isComposing) return;
      // Some testing-library callers set `isComposing` on the synthetic
      // event itself rather than the native one.
      const synthetic = e as KeyboardEvent<HTMLTextAreaElement> & {
        isComposing?: boolean;
      };
      if (synthetic.isComposing) return;

      e.preventDefault();
      performSubmit();
    },
    [performSubmit],
  );

  // -----------------------------------------------------------------------
  // Derived UI bits
  // -----------------------------------------------------------------------

  /**
   * `displayError` prefers the blocked reason; otherwise it surfaces the
   * textarea's over-length state so the user understands why the submit
   * button is disabled. Listing the count gives them a target ("I need to
   * cut 3 characters"). Send failures are no longer shown here — they appear
   * as a failed bubble in the transcript with a retry button.
   */
  const displayError: string | null = blockedReason
    ? blockedReason
    : tooLong
      ? `メッセージが長すぎます。${maxLength}文字以内に収めてください。（現在 ${content.length} 文字）`
      : null;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <form className="message-input" onSubmit={handleSubmit} noValidate>
      <label
        htmlFor="message-input-textarea"
        className="message-input__label"
      >
        メッセージ
      </label>

      <div className="message-input__row">
        <textarea
          id="message-input-textarea"
          ref={textareaRef}
          className="message-input__textarea"
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          disabled={blocked}
          /* rows=1 で起動し、改行が入ったときに useLayoutEffect が scrollHeight
           * 分だけ縦に伸ばす。テンプレートリストの直下に置く都合上、初期
           * 高さは 1 行ぶんに留めて画面占有を抑える。 */
          rows={1}
          // Native `maxLength` would silently truncate; we let the user
          // type beyond it and surface an inline error so they're aware.
          placeholder={
            blocked
              ? `${ownerNickname ?? 'オーナー'}さんからの返信を待っています…`
              : 'メッセージを入力'
          }
          aria-describedby="message-input-error"
        />

        <button
          type="submit"
          className="message-input__submit"
          disabled={!canSubmit}
          aria-label="メッセージを送信"
        >
          {/* 紙飛行機アイコン (Material/Feather 系の典型形状)。
           * currentColor で var(--on-primary) を引き継ぐので、テーマ切替時も
           * ボタン文字色に同期する。 */}
          <svg
            className="message-input__submit-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M3.4 20.4L21.85 12.92a1 1 0 0 0 0-1.84L3.4 3.6a1 1 0 0 0-1.38 1.21L4.5 12 2.02 19.19a1 1 0 0 0 1.38 1.21zM6 13l-1.2 3.46L17.5 12 4.8 7.54 6 11l8 1-8 1z"
            />
          </svg>
        </button>
      </div>

      {/*
       * `role="alert"` + `aria-live="polite"` lets the message be announced
       * after the user's keystrokes settle instead of interrupting them.
       * Rendered always (even when empty) so assistive tech doesn't have to
       * discover a brand-new node on each error.
       */}
      <div
        id="message-input-error"
        className="message-input__error"
        role="alert"
        aria-live="polite"
      >
        {displayError}
      </div>
      {/* Quota hint sits below the error region so a real error never gets
          hidden behind it. Plain text, no live region — it changes whenever
          the parent decides, not in response to user input. */}
      {quotaHint !== null && displayError === null && (
        <div className="message-input__quota" aria-live="polite">
          {quotaHint}
        </div>
      )}
    </form>
  );
}
